import React, { useMemo, useState } from 'react';
import { Button, Modal, SparkIcon, SparkModalPortal, Tabs, Text } from '@spark';
import { handleCopy } from '@/shared/utils/helpers';
import DiffText, { diffPlainText } from './DiffText';
import EntityHighlightedText from './EntityHighlightedText';
import ClampedContent from './ClampedContent';
import {
  AnnotationTagRow,
  LabelTransition,
  buildChangesSummaryTags,
  buildSingleLabelTransition,
  buildVersionTags,
} from './annotationTags';
import './versionsDiff.css';

/** Section header; `copyText` adds an icon button that copies that section's plain text. */
const SectionTitle = ({ children, copyText }) => (
  <div className="vdm-record-section-header">
    <Text variant="base-semibold" color="primary" as="p" className="vdm-record-section-title">
      {children}
    </Text>
    {copyText ? (
      // Icon-only Spark Buttons render their own tooltip (from aria-label).
      <Button
        variant="ghost"
        size="tiny"
        type="button"
        aria-label="Copy"
        icon={<SparkIcon slug="copy" size="small" />}
        onClick={() => handleCopy(copyText)}
      />
    ) : null}
  </div>
);

/**
 * Merged word-diff plus each version's plain text — the shared layout of the
 * "Changed Example" and "Changed Summary" / "Changed Output" tabs.
 */
const DiffSections = ({ segments, fromLabel, toLabel }) => (
  <div className="vdm-record-sections">
    {/* No copy button here — the merged diff isn't a real version's text. */}
    <section>
      <SectionTitle>Changes Summary</SectionTitle>
      <DiffText segments={segments} view="both" />
    </section>
    {/* Per-version sections show each version's plain text —
        diff coloring lives only in the Changes Summary above. */}
    <section>
      <SectionTitle copyText={diffPlainText(segments, 'from')}>{fromLabel}</SectionTitle>
      <DiffText segments={segments} view="from" plain />
    </section>
    <section>
      <SectionTitle copyText={diffPlainText(segments, 'to')}>{toLabel}</SectionTitle>
      <DiffText segments={segments} view="to" plain />
    </section>
  </div>
);

/**
 * Nested "Record Diff" modal (Figma nodes 5227:14412 / 5212:37680): "Changed
 * Example" tab with the merged diff plus each version's text, and "Changed
 * Entities" / "Changed Labels" tab with per-version annotations and a summary.
 * On generative tasks (`outputLabel` set) the annotations tab is replaced by a
 * "Changed Summary" / "Changed Output" tab diffing the response text the same
 * way as the example.
 */
const RecordDiffModal = ({
  record,
  fromLabel,
  toLabel,
  outputLabel = null,
  singleLabel = false,
  allLabels = [],
  onClose,
}) => {
  const isEntities = Boolean(record.from?.entities?.length || record.to?.entities?.length);
  // Single-label classification: a label switch reads as `old → new`, and
  // label tags drop the redundant `(1)` count everywhere.
  const labelTransition = singleLabel ? buildSingleLabelTransition(record) : null;
  const hideCounts = singleLabel;
  const hasTextChange = Boolean(record.text_diff?.length);
  const hasResponseChange = Boolean(record.response_diff?.length);
  const hasAnnotationChange =
    Boolean(record.annotations) || record.status === 'added' || record.status === 'removed';

  // A tab with nothing to diff is omitted entirely (not just disabled).
  const tabs = useMemo(() => {
    const items = [];
    if (hasTextChange) {
      items.push({ id: 'example', label: 'Changed Example' });
    }
    if (outputLabel) {
      // Generative tasks have no labels/entities to diff — their annotations
      // tab would always be empty, so the response diff replaces it.
      if (hasResponseChange) {
        items.push({ id: 'response', label: `Changed ${outputLabel}` });
      }
    } else if (hasAnnotationChange) {
      items.push({
        id: 'annotations',
        label: isEntities ? 'Changed Entities' : 'Changed Labels',
      });
    }
    return items;
  }, [hasTextChange, hasResponseChange, hasAnnotationChange, isEntities, outputLabel]);

  const [activeTab, setActiveTab] = useState(tabs[0]?.id);

  const renderVersionAnnotations = (payload, otherPayload, { markNew }) => {
    if (!payload) {
      return (
        <Text variant="base-regular" color="secondary">
          Not present in this version
        </Text>
      );
    }
    return (
      <>
        <ClampedContent maxLines={5}>
          {isEntities ? (
            <EntityHighlightedText
              text={payload.text}
              entities={payload.entities}
              otherSpans={otherPayload?.entities || []}
              allLabels={allLabels}
            />
          ) : (
            <Text variant="base-regular" color="primary">{payload.text}</Text>
          )}
        </ClampedContent>
        <AnnotationTagRow
          tags={buildVersionTags(payload, otherPayload, { markNew, hideCounts })}
          allLabels={allLabels}
          maxVisible={12}
        />
      </>
    );
  };

  return (
    <SparkModalPortal open onClose={onClose} width={1024} destroyOnClose>
      <Modal
        title="Record Diff"
        showSubtitle={false}
        fixedHeight={false}
        showMainButton={false}
        tertiaryLabel="Close"
        className="vdm-record-modal"
        onTertiaryClick={onClose}
        onCloseClick={onClose}
      >
        <div className="vdm-record-body">
          <Tabs items={tabs} activeId={activeTab} onChange={setActiveTab} size="medium" />
          {activeTab === 'example' ? (
            <DiffSections segments={record.text_diff} fromLabel={fromLabel} toLabel={toLabel} />
          ) : null}
          {activeTab === 'response' ? (
            <DiffSections
              segments={record.response_diff}
              fromLabel={fromLabel}
              toLabel={toLabel}
            />
          ) : null}
          {activeTab === 'annotations' ? (
            <div className="vdm-record-sections">
              <section>
                <SectionTitle>{fromLabel}</SectionTitle>
                {renderVersionAnnotations(record.from, record.to, { markNew: false })}
              </section>
              <section>
                <SectionTitle>{toLabel}</SectionTitle>
                {renderVersionAnnotations(record.to, record.from, { markNew: true })}
              </section>
              <section>
                <SectionTitle>Changes Summary</SectionTitle>
                {labelTransition ? (
                  <LabelTransition transition={labelTransition} allLabels={allLabels} />
                ) : (
                  <AnnotationTagRow
                    tags={buildChangesSummaryTags(record, { hideCounts })}
                    allLabels={allLabels}
                    maxVisible={20}
                  />
                )}
              </section>
            </div>
          ) : null}
        </div>
      </Modal>
    </SparkModalPortal>
  );
};

export default RecordDiffModal;
